import intersection from 'lodash.intersection'
import isEmpty from 'lodash.isempty'
import { Observable, Subject, Subscription } from 'rxjs'
import { Ref, ref } from 'vue'

import { ApiClientV2 } from '@/api/ApiClientV2'
import {
  DeviceAssignment,
  EquipmentVariant,
} from '@/apps/brelag/common/models/equipment'
import {
  KnockautProject,
  KnockautProjectPatchSerializer,
} from '@/apps/brelag/common/models/knockaut'
import { Project } from '@/apps/brelag/common/models/project'
import { EquipmentHandler } from '@/apps/brelag/mandator-user/project-editor/equipmentHandler'
import { useFlowScriptEditorStore } from '@/store/FlowScriptEditorStore'
import { useKnockautStore } from '@/store/KnockautStore'
import { WebSocketMessageType } from '@/util/knockautApiClient/constants'
import { WebSocketMessage } from '@/util/knockautApiClient/interfaces'
import { KnockautApiClient } from '@/util/knockautApiClient/KnockautApiClient'
import { simpleCopy } from '@/util/util'

import { KnockautView } from '../../../../../components/ag-grid/AgGridKnockautMixin'
import { exportConfig } from '../../components/editor/egate/eGateExporter'
import { DeviceHandler } from '../deviceHandler'
import { DeviceTree, ProjectEditor } from '../projectEditor'
import { DOMINO_SWISS_MODULE_IDS } from './codeBlockEditor/codeBlockFunctions'
import { DashboardHandler } from './dashboard'
import { EventHandler, KnockautEvent } from './event'
import {
  ARCHIVE_CONTROL_MODULE_ID,
  BRELAG_CONFIGURATOR_MODULE_ID,
  BRELAG_CONFIGURATOR_MODULE_NAME,
  BRELAG_DOMINO_SWISS_MODULE_ID,
  BRELAG_DOMINO_SWISS_MODULE_URL,
  CONNECT_MODULE_ID,
  CreateSymconObjectModel,
  DASHBOARD_CONFIGURATOR_MODULE_ID,
  DUMMY_MODULE_ID,
  EVENTS_FOLDER_NAME,
  KNOCKAUT_CONFIGURATOR_MODULE_ID,
  KnockautAction,
  KnockautConfigurationForm,
  KnockautInstance,
  KnockautObject,
  LOCATION_MODULE_ID,
  RawKnockautProfile,
  RawKnockautSnapshot,
  SCRIPTS_FOLDER_NAME,
  SymconCategory,
  SymconConnectionError,
  SymconEnvironment,
  SymconLocation,
  SymconModuleBranchInfo,
  SymconModuleType,
  SymconNetworkInfo,
  SymconObject,
  SymconObjectType,
  SymconScriptType,
  SymconVariableType,
  SymconVariableValueType,
  UTIL_CONTROL_MODULE_ID,
} from './interfaces'
import { KnockautDeviceHandler } from './knockautDeviceHandler'
import { KnockautModuleHandler } from './knockautModuleHandler'
import { ArchiveHandler, ArchiveModule } from './modules/archive'
import { KNOCKAUT_X_MODULE_IDS } from './modules/constants'
import { UtilControlHandler, UtilControlModule } from './modules/utilControl'
import { SymOSHandler } from './symOS'
import { transformIdToNumber } from './util'
import { WebSocketHandler } from './websocketHandler'

export interface ObjectTreeConfiguration {
  includeObjectTree?: boolean
  includeDevices?: boolean
  includeScripts?: boolean
  includeLinks?: boolean
  includeEvents?: boolean
  includeVariables?: boolean

  // If true, objects with property 'hidden=true' will be included in tree
  showHiddenObjects?: boolean

  // If true, will hide empty folders
  showEmptyFolders?: boolean

  deviceDisabled?: boolean
  scriptDisabled?: boolean
  linkDisabled?: boolean
  eventDisabled?: boolean
  categoryDisabled?: boolean
  variableDisabled?: boolean
}

export class KnockautHandler {
  static instance: KnockautHandler = null

  apiClient: ApiClientV2
  private _equipmentHandler: EquipmentHandler
  private _deviceHandler: DeviceHandler
  projectEditor: ProjectEditor
  knockautClient: KnockautApiClient
  knockautDeviceHandler: KnockautDeviceHandler
  webSocketHandler: WebSocketHandler
  private _snapshot: RawKnockautSnapshot

  archiveHandler: ArchiveHandler
  utilControlHandler: UtilControlHandler
  dashboardHandler: DashboardHandler
  symOSHandler: SymOSHandler
  eventHandler: EventHandler
  moduleHandler: KnockautModuleHandler

  // Everything related to actual fusionONE
  rootFolderId: number = 0
  scriptsFolderId: number = null
  eventsFolderId: number = null
  dashboardBaseIds: number[] = []
  private _dominoSwissConfiguratorId?: number = null
  private _knockautConfiguratorId?: number = null
  private _locationConfiguratorId?: number = null
  private _links: KnockautObject[] = []
  private _deviceGroups: KnockautObject[] = []
  private _dashboards = ref<KnockautObject[]>([])
  private _variables: Map<number, KnockautObject[]> = new Map() // 'number' is the parentID of the variable
  private _profiles: Map<string, RawKnockautProfile> = new Map()
  private _scripts: KnockautObject[] = []
  private _flowscripts: KnockautObject[] = []
  private _events: KnockautObject[] = []
  private _categories: KnockautObject[] = []
  private _actions: KnockautAction[] = []

  // These are used from the .vue files to display the list
  public dashboardTree: any[] = []

  // Keep this immutable
  private objectTree: KnockautInstance[] = []
  private dataTree: KnockautObject[] = []

  // Everything related to individual KnockautProjet Editor
  public initialized = ref(false)
  private _knockautProject = ref<KnockautProject>(null)
  private _project: Project = null
  private _fusionOne: DeviceAssignment = null
  private _deviceTree: DeviceTree[] = null

  private _fusionOneEquipments: string[] = []
  private _fusionOneEquipmentVariants: EquipmentVariant[] = []
  private _fusionOnes: Ref<DeviceAssignment[]> = ref([])

  // Can be used to force refresh when tree is being refreshed
  public refreshCounter = 0
  private refreshSubject: Subject<void> = new Subject()
  public getRefreshObservable(): Observable<void> {
    return this.refreshSubject.asObservable()
  }

  private objectChangedSubject: Subject<KnockautObject> = new Subject()
  private objectChangedSubscription: Subscription = null

  public getObjectChangedObservable(): Observable<KnockautObject> {
    return this.objectChangedSubject.asObservable()
  }

  // Used in Object tree
  public selectedNodes: KnockautObject[] = []

  public get fusionOnes(): Ref<DeviceAssignment[]> {
    return this._fusionOnes
  }

  public get fusionOne(): DeviceAssignment {
    return this._fusionOne
  }

  public get deviceGroups(): KnockautObject[] {
    return this._deviceGroups
  }

  public get links(): KnockautObject[] {
    return this._links
  }

  public get dashboards(): KnockautObject[] {
    return this._dashboards.value
  }

  public get events(): KnockautObject[] {
    return this._events
  }

  public get scripts(): KnockautObject[] {
    return this._scripts
  }

  public get flowscripts(): KnockautObject[] {
    return this._flowscripts
  }

  get variables(): Map<number, KnockautObject[]> {
    return this._variables
  }

  public get categories(): KnockautObject[] {
    return this._categories
  }

  public get actions(): KnockautAction[] {
    return this._actions
  }

  constructor() {
    this.projectEditor = ProjectEditor.getInstance()
    this._equipmentHandler = this.projectEditor.equipmentHandler
    this._deviceHandler = this.projectEditor.deviceHandler
    this.symOSHandler = new SymOSHandler(this)
    this.knockautDeviceHandler = new KnockautDeviceHandler(this)
    this.webSocketHandler = new WebSocketHandler(this)
    KnockautHandler.instance = this
  }

  static getInstance() {
    if (!KnockautHandler.instance) {
      KnockautHandler.instance = new KnockautHandler()
    }
    return KnockautHandler.instance
  }

  /**
   * Returns true if obj is inside the scripts folder
   */
  isInsideScripts(obj: KnockautObject): boolean {
    return obj.parentPath.includes(this.scriptsFolderId)
  }

  /**
   * Returns true if obj is inside the events folder
   */
  isInsideEvents(obj: KnockautObject): boolean {
    return obj.parentPath.includes(this.eventsFolderId)
  }

  /**
   * Returns true if the folder is empty or only contains other folders
   */
  isEmptyFolder(folderId: number): boolean {
    const folder = this.getObjectById(folderId)
    if (folder.type !== SymconObjectType.CATEGORY) {
      return false
    }
    for (const obj of this.dataTree) {
      if (obj.parentID === folderId) {
        // obj is a child of our folder. Only other empty folders are allowed as children
        const isEmpty = this.isEmptyFolder(obj.id)
        if (!isEmpty) {
          return false
        }
      }
    }
    return true
  }

  getLinkTree(options: ObjectTreeConfiguration = {}): KnockautObject[] {
    const out = []
    for (const obj of this.dataTree) {
      // Remove dashboards
      if (this.dashboardHandler.isInsideDashboard(obj)) {
        continue
      }
      out.push(obj)
    }
    return this.applyTreeConfig(out, options)
  }

  /**
   * Retrieves all instances that belong to the module with id moduleId (and the corresponding folders)
   * @param view current view
   * @param options tree options
   * @returns filtered data tree
   */
  getInstancesTree(
    view: KnockautView,
    options: ObjectTreeConfiguration = {}
  ): KnockautObject[] {
    options = {
      showEmptyFolders: true,
      ...options,
    }
    let moduleIds = []
    const ids: Set<number> = new Set()
    if (view === 'dummy') {
      moduleIds = [DUMMY_MODULE_ID]
    } else if (view === 'dominoswiss') {
      moduleIds = DOMINO_SWISS_MODULE_IDS
    } else if (view === 'knockautx') {
      moduleIds = KNOCKAUT_X_MODULE_IDS
    } else {
      moduleIds = null
      // Add location module
      ids.add(this._locationConfiguratorId)
    }
    const out = []
    // Find all instances of this module and the parents
    for (const obj of this.dataTree) {
      if (
        (!moduleIds &&
          obj.type === SymconObjectType.INSTANCE &&
          obj.data.moduleType === SymconModuleType.DEVICE) ||
        this.knockautDeviceHandler.isInstanceOfModule(moduleIds, obj)
      ) {
        // Add all instances of allowed modules
        ids.add(obj.id)
        // Add the parents of the instance
        for (const parent of obj.parentPath) {
          ids.add(parent)
        }
      }
      // Add any empty folders that are not: Dashboard, EventFolder, ScriptFolder (or inside one of them)
      if (
        options.showEmptyFolders &&
        this.isEmptyFolder(obj.id) &&
        !this.isInsideEvents(obj) &&
        !this.isInsideScripts(obj) &&
        !this.dashboardHandler.isInsideDashboard(obj)
      ) {
        // Add parents of empty folder as well
        for (const id of obj.parentPath) {
          ids.add(id)
        }
      }
    }

    // Add variables of instances
    for (const obj of this.dataTree) {
      if (obj.type === SymconObjectType.VARIABLE && ids.has(obj.parentID)) {
        ids.add(obj.id)
      }
    }

    // Actually add the objects
    for (const obj of this.dataTree) {
      if (ids.has(obj.id)) {
        out.push(obj)
      }
    }

    return this.applyTreeConfig(out, options)
  }

  _removeFirstParent(obj: KnockautObject): KnockautObject {
    // Need to create copy so that original parentPath is not changed when reloaded
    const copy = simpleCopy(obj)
    copy.parentPath = copy.parentPath.slice(1)
    return copy
  }

  getEventTree(options: ObjectTreeConfiguration = {}): KnockautObject[] {
    // Root folder for all events: _eventsFolderId
    const out = []
    // Only show objects that have one of the base ids in their parent path
    for (const obj of this.dataTree) {
      if (!isEmpty(intersection(obj.parentPath, [this.eventsFolderId]))) {
        if (obj.id !== this.eventsFolderId) {
          // In the event view we only want to show what is inside the eventFolder
          // -> We have to remove the eventFolderId from the parentPath, otherwise it doesnt work
          out.push(this._removeFirstParent(obj))
        }
      }
    }
    return this.applyTreeConfig(out, options)
  }

  getScriptTree(options: ObjectTreeConfiguration = {}): KnockautObject[] {
    // Root folder for all scripts: scriptsFolderId
    const out = []
    // Only show objects that have one of the base ids in their parent path
    for (const obj of this.dataTree) {
      if (!isEmpty(intersection(obj.parentPath, [this.scriptsFolderId]))) {
        if (obj.id !== this.scriptsFolderId) {
          // In the script view we only want to show what is inside the scriptsFolder
          // also we only show scripts that are not of type FLOW
          // -> We have to remove the scriptsFolderId from the parentPath, otherwise it doesnt work
          if (
            (obj.type === SymconObjectType.SCRIPT.valueOf() &&
              obj.data.type !== SymconScriptType.FLOW.valueOf()) ||
            obj.type === SymconObjectType.CATEGORY.valueOf()
          ) {
            out.push(this._removeFirstParent(obj))
          }
        }
      }
    }
    return this.applyTreeConfig(out, options)
  }

  getFlowScriptTree(options: ObjectTreeConfiguration = {}): KnockautObject[] {
    // Root folder for all scripts: scriptsFolderId
    const out = []
    // Only show objects that have one of the base ids in their parent path
    for (const obj of this.dataTree) {
      if (!isEmpty(intersection(obj.parentPath, [this.scriptsFolderId]))) {
        if (obj.id !== this.scriptsFolderId) {
          // In the event view we only want to show what is inside the scriptsFolder
          // also we only show scripts of type FLOW
          // -> We have to remove the scriptsFolderId from the parentPath, otherwise it doesnt work
          if (
            obj.type === SymconObjectType.SCRIPT.valueOf() &&
            obj.data.type !== SymconScriptType.FLOW.valueOf()
          ) {
            continue
          }
          out.push(this._removeFirstParent(obj))
        }
      }
    }
    return this.applyTreeConfig(out, options)
  }

  getDashboardTree(options: ObjectTreeConfiguration = {}): KnockautObject[] {
    const out = []
    // Only show objects that have one of the base ids in their parent path
    for (const obj of this.dataTree) {
      if (!isEmpty(intersection(obj.parentPath, this.dashboardBaseIds))) {
        out.push(obj)
      }
    }
    return this.applyTreeConfig(out, options)
  }

  getVisualisations(withWebfront = true, withTileVisu = true) {
    const webfrontModuleID = '{3565B1F2-8F7B-4311-A4B6-1BF1D868F39E}'
    const tileVisuModuleID = '{B5B875BB-9B76-45FD-4E67-2607E45B3AC4}'
    return this.dataTree.filter(
      (obj) =>
        obj.type === SymconObjectType.INSTANCE &&
        obj.data.moduleType === SymconModuleType.VISUALIZER &&
        ((withWebfront && obj.data.moduleID === webfrontModuleID) ||
          (withTileVisu && obj.data.moduleID === tileVisuModuleID))
    )
  }

  applyTreeConfig(
    input: KnockautObject[],
    options: ObjectTreeConfiguration
  ): KnockautObject[] {
    // Filter hidden objects
    let visibleObjects: KnockautObject[] = []
    if (!options.showHiddenObjects) {
      const hiddenObjectsIds: number[] = []
      for (const obj of input) {
        if (obj.hidden) {
          hiddenObjectsIds.push(obj.id)
        } else {
          visibleObjects.push(obj)
        }
      }
      // Hide all children of hidden objects
      visibleObjects = visibleObjects.filter((obj) => {
        return isEmpty(intersection(obj.parentPath, hiddenObjectsIds))
      })
      /*
      input = input.filter((data) => {
        return !data.hidden
      })
      */
    } else {
      visibleObjects = input
    }

    visibleObjects.sort((a, b) => {
      // Sort by position
      // If the first item has a position, move it down
      // If the first item has a position, move it up
      if (a.position > b.position) return 1
      if (a.position < b.position) return -1

      // If the position is the same between both items, sort by type
      if (a.type > b.type) return 1
      if (a.type < b.type) return -1

      // If the position and type is the same between both items, sort alphabetically
      // If the first item comes first in the alphabet, move it up
      // Otherwise move it down
      if (a.name > b.name) return 1
      if (a.name < b.name) return -1
    })
    return visibleObjects
  }

  getProjectStructureTree(
    options: ObjectTreeConfiguration = {}
  ): KnockautObject[] {
    const out = simpleCopy(this.dataTree)
    return this.applyTreeConfig(out, options)
  }

  getObjectFromDataTree(id: number): KnockautObject {
    return this.dataTree.find((obj) => obj.id === id)
  }

  getChildrenFromDataTree(id: number): KnockautObject[] {
    return this.dataTree.filter((obj) => {
      if (obj.parentPath.includes(id) && obj.id !== id) {
        return obj
      }
    })
  }

  buildDataTree(options: ObjectTreeConfiguration = {}) {
    // This creates a map to get the direct (next-higher) parent of an object
    const directParentMap: Map<number, number> = new Map()
    for (const [key, value] of Object.entries(this._snapshot?.objects ?? {})) {
      const id = transformIdToNumber(key)
      if (id === 0) {
        // Special case
        directParentMap.set(id, null)
      } else if (!directParentMap.has(id)) {
        directParentMap.set(id, value.parentID)
      }
    }

    const getParentsOf = (
      id: number,
      currParrents: number[] = []
    ): number[] => {
      const directParent = directParentMap.get(id)
      if (directParent === 0) {
        return currParrents
      } else {
        const newParents = [directParent, ...currParrents]
        return getParentsOf(directParent, newParents)
      }
    }

    // Create a full parent path for each object
    // e.g.
    // Given tree structure: Root [id: 0] - Folder [id: 3] - Subfolder [id: 6] - Object [id: 2]
    // the entries would be:
    // 2: [2, 6, 3]
    // 6: [6, 3]
    // 3: [3]
    // The root folder itself is never included
    const fullParentMap: Map<number, number[]> = new Map()
    for (const [key, value] of Object.entries(this._snapshot?.objects ?? {})) {
      const id = transformIdToNumber(key)
      if (id === 0) {
        continue
      } else {
        const parents = getParentsOf(id, [id])
        fullParentMap.set(id, parents)
      }
    }

    const fullDataTree: KnockautObject[] = []
    for (const [key, value] of Object.entries(this._snapshot?.objects ?? {})) {
      const id = transformIdToNumber(key)
      if (id === 0) {
        continue
      }
      fullDataTree.push({
        ...value,
        id,
        parentPath: fullParentMap.get(id).map((value) => value),
      })
    }
    this.dataTree = fullDataTree
  }

  async refreshFusionOnes(apiClient: ApiClientV2) {
    const fusionOnes = []
    const knockautInfo = await KnockautProject.getInfo(apiClient)
    this._fusionOneEquipments = [knockautInfo.fusion_one_equipment_id]
    this._fusionOneEquipmentVariants = [
      this._equipmentHandler.getVariantFromId(
        knockautInfo.fusion_one_equipment_variant_id
      ),
    ]

    // Find all FusionOne devices
    this.projectEditor.receivers.forEach((assignment) => {
      if (this.isFusionOne(assignment.equipment)) {
        fusionOnes.push(assignment)
      }
    })
    this._fusionOnes.value.splice(
      0,
      this._fusionOnes.value.length,
      ...fusionOnes
    )
  }

  async getKnockautXUserInfo(): Promise<{
    user_id: string
    email: string
  }> {
    const userInfo = await KnockautProject.getKnockautXUserInfo(
      this.apiClient,
      this.project.id
    )
    return userInfo
  }

  closeKnockautEditor() {
    this.knockautClient.closeWebSocket()
    this.knockautClient.removeListener()
    const knockautStore = useKnockautStore()
    knockautStore.$reset()
    const flowScriptStore = useFlowScriptEditorStore()
    flowScriptStore.$reset()
    this._reset()
  }

  /**
   * Loads Knockaut Project into editor
   * @param knockautProjectId
   */
  async initKnockautEditor(
    knockautProjectId: string,
    apiClient: ApiClientV2
  ): Promise<void> {
    this.apiClient = apiClient
    this.initialized.value = false

    this._knockautProject.value = await this.apiClient.get<KnockautProject>(
      KnockautProject,
      knockautProjectId
    )
    // TODO: This is temporary hard-coded password for development.
    // Only works with Severin's Controller
    // this._knockautProject.value.password = 'sQC@4zu4Ev/G'

    if (!this.projectEditor.isInitialized.value) {
      await this.projectEditor.loadProject(
        this._knockautProject.value.project,
        apiClient
      )
    }
    this._project = this.projectEditor.projectObject

    if (this._knockautProject.value.symos_password) {
      this.symOSHandler.setSymOSPassword(
        this._knockautProject.value.symos_password
      )
    } else {
      this.symOSHandler.setSymOSPassword(this._knockautProject.value.password)
    }

    this._fusionOne = await this.apiClient.get<DeviceAssignment>(
      DeviceAssignment,
      this._knockautProject.value.fusion_one
    )

    await this.projectEditor.selectReceiver(this._fusionOne.id)

    // Init KnockautClient with WebSocket
    const host = this._knockautProject.value.remote_access_link
    const hostinfo = {
      secure: !host.includes(':'),
      host: host,
      protocol: window.location.protocol,
    }
    const apiOptions = {
      host: hostinfo.protocol + '//' + hostinfo.host,
      username: 'webfront',
    }
    this.knockautClient = new KnockautApiClient(
      apiOptions,
      this.webSocketHandler.getWebsocketOptions(hostinfo),
      this.webSocketHandler
    )
    this.knockautClient.setCredentials({
      username: this._knockautProject.value.username,
      password: this._knockautProject.value.password,
    })
    this.knockautClient.connectWebSocket()
    this.initRealtimeUpdates()

    try {
      // Test conection
      await this.parseKnockautResponse('IPS_GetSnapshot', [])
    } catch (error) {
      // Connection not possible
      throw new SymconConnectionError('Keine Verbindung zur FusionONE möglich.')
    }
    await this._setupSymcon()
    this.initialized.value = true
  }

  initRealtimeUpdates() {
    // TODO: Variable updates

    this.objectChangedSubscription = this.webSocketHandler
      .getObjectChanges()
      .subscribe(async (message: WebSocketMessage) => {
        const id = message.SenderID
        const messageType = message.Message
        let updateChildren = false
        let updateDataTree = false
        // Replace data in snapshot
        if (this._getObjectById(id) === null) {
          // This can happen if the object was just created and not added to the snapshot yet...
          // TODO: Improve the create object functions so they work better
          return
        }
        if (messageType === WebSocketMessageType.OM_CHANGENAME) {
          // name changed
          this._snapshot.objects[`ID${id}`].name = message.Data[0] as string
          updateDataTree = true
        } else if (messageType === WebSocketMessageType.OM_CHANGEICON) {
          // Icon changed
          this._snapshot.objects[`ID${id}`].icon = message.Data[0] as string
          updateDataTree = true
        } else if (messageType === WebSocketMessageType.OM_CHANGEPARENT) {
          this._snapshot.objects[`ID${id}`].parentID = message.Data[0] as number
          // If the moved object has children, those have to be refreshed as well
          updateChildren = true
          updateDataTree = true
        } else {
          // TODO: Replace object
        }

        if (updateDataTree) {
          // Update Data Tree for Grid View
          // TOOD: Replace in-place or re-build?
          this.buildDataTree()
          const obj = this.getObjectFromDataTree(id)
          this.objectChangedSubject.next(obj)
          if (updateChildren) {
            const children = this.getChildrenFromDataTree(id)
            for (const child of children) {
              this.objectChangedSubject.next(child)
            }
          }
        }
      })
  }

  unsubscribeRealtimeUpdates() {
    if (this.objectChangedSubscription) {
      this.objectChangedSubscription.unsubscribe()
      this.objectChangedSubscription = null
    }
  }

  /**
   * Saves connection parameters to connect to FusionONE
   * @param remoteAccessLink
   * @param username
   * @param password
   */
  async saveConnectionParams(
    username: string,
    password: string,
    remoteAccessLink?: string,
    licenseKey?: string
  ): Promise<void> {
    const projectChanges = {
      id: this.project.id,
      username: username,
      password: password,
    }
    if (remoteAccessLink) {
      projectChanges['remote_access_link'] = remoteAccessLink
    }
    if (licenseKey) {
      projectChanges['license_key'] = licenseKey
    }
    this._knockautProject.value = await this.apiClient.update<
      KnockautProjectPatchSerializer,
      KnockautProject
    >(KnockautProject, projectChanges)
  }

  async changeSymOsPassword(
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    await KnockautProject.changeSymOsPassword(
      this.apiClient,
      this.project.id,
      oldPassword,
      newPassword
    )
    this._knockautProject.value = await this.apiClient.update<
      KnockautProjectPatchSerializer,
      KnockautProject
    >(KnockautProject, {
      id: this.project.id,
      symos_password: newPassword,
    })
  }

  async changeRemoteAccessPassword(
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    // Do manual check for oldPassword
    if (oldPassword !== this.project.password) {
      throw new Error('Altes Passwort stimmt nicht')
    }

    await KnockautProject.changeRemoteAccessPassword(
      this.apiClient,
      this.project.id,
      oldPassword,
      newPassword
    )
    this._knockautProject.value = await this.apiClient.update<
      KnockautProjectPatchSerializer,
      KnockautProject
    >(KnockautProject, {
      id: this.project.id,
      password: newPassword,
    })
    await this.symOSHandler.restart()
    this.knockautClient.setCredentials({
      username: this._knockautProject.value.username,
      password: this._knockautProject.value.password,
    })
  }

  private _reset() {
    this.scriptsFolderId = null
    this.eventsFolderId = null
    this._dominoSwissConfiguratorId = null
    this._knockautConfiguratorId = null
    this._locationConfiguratorId = null
    this._deviceGroups = null
    this._links = null
    this._dashboards.value = null
    this.dashboardBaseIds = null
    this._scripts = null
    this._categories = null
    this._flowscripts = null
    this._events = null

    this.dashboardTree = null
  }

  /**
   * Checks that everything that is needed is present and creates if something is missnig
   */
  private async _setupSymcon() {
    this._reset()

    // Load snapshot
    this._snapshot = await this.parseKnockautResponse('IPS_GetSnapshot', [])
    console.log('Snapshot: ', this._snapshot)

    if (this._snapshot?.options.ServerSecurity === true) {
      // Set ServerSecurity to false to allow CORS
      await this.parseKnockautResponse('IPS_SetOption', [
        'ServerSecurity',
        false,
      ])
    }
    if (this._snapshot?.options.ThreadCount < 50) {
      // Set ThreadCount to 50
      await this.parseKnockautResponse('IPS_SetOption', ['ThreadCount', 50])
    }

    for (const [key, obj] of Object.entries(this._snapshot?.objects ?? {})) {
      if (obj.name === SCRIPTS_FOLDER_NAME) {
        this.scriptsFolderId = transformIdToNumber(key)
      }
      if (obj.name === EVENTS_FOLDER_NAME) {
        this.eventsFolderId = transformIdToNumber(key)
      }

      if (
        obj.data &&
        obj.data.moduleType === SymconModuleType.CONFIGURATOR.valueOf() &&
        obj.data.moduleName === BRELAG_CONFIGURATOR_MODULE_NAME
      ) {
        this._dominoSwissConfiguratorId = transformIdToNumber(key)
      }

      if (
        obj.data &&
        obj.data.moduleType === SymconModuleType.CONFIGURATOR.valueOf() &&
        obj.data.moduleID === KNOCKAUT_CONFIGURATOR_MODULE_ID
      ) {
        this._knockautConfiguratorId = transformIdToNumber(key)
      }

      if (obj.data && obj.data.moduleID === LOCATION_MODULE_ID) {
        this._locationConfiguratorId = transformIdToNumber(key)
      }

      // Check that the connect module has parameter 'Active' set to true.
      // This is needed so that the api is accessible from outside the local network.
      if (obj.data && obj.data.moduleID === CONNECT_MODULE_ID) {
        const connectId = transformIdToNumber(key)
        if (
          obj.data.configuration &&
          obj.data.configuration.hasOwnProperty('Active') &&
          obj.data.configuration.Active === false
        ) {
          const newConfig = simpleCopy(obj.data.configuration)
          newConfig.Active = true
          await this.parseKnockautResponse('IPS_SetConfiguration', [
            connectId,
            JSON.stringify(newConfig),
          ])
          await this.parseKnockautResponse('IPS_ApplyChanges', [connectId])
        }
      }

      if (obj.data && obj.data.moduleID === ARCHIVE_CONTROL_MODULE_ID) {
        this.archiveHandler = new ArchiveHandler(this, {
          id: transformIdToNumber(key),
          ...(obj as ArchiveModule),
        })
      }

      if (obj.data && obj.data.moduleID === UTIL_CONTROL_MODULE_ID) {
        this.utilControlHandler = new UtilControlHandler(this, {
          id: transformIdToNumber(key),
          ...(obj as UtilControlModule),
        })
        await this.utilControlHandler.enableRequestLicenseData()
      }
    }

    // Check that 'Dominoswiss' module is installed. Install it if not present.
    const instanceList = await this.parseKnockautResponse(
      'IPS_GetInstanceListByModuleID',
      [BRELAG_DOMINO_SWISS_MODULE_ID]
    )
    // Instance should always have length 1
    if (instanceList.length !== 1) {
      throw new Error(
        '"IPS_GetInstanceListByModuleID" hat mehr als eine Antwort geliefert. Bitte kontaktieren sie einen Admin.'
      )
    }

    const moduleList = await this.parseKnockautResponse('MC_GetModuleList', [
      instanceList[0],
    ])
    if (!moduleList.includes('KnockautTitan_Dominoswiss')) {
      // Install the module
      await this.parseKnockautResponse('MC_CreateModule', [
        instanceList[0],
        BRELAG_DOMINO_SWISS_MODULE_URL,
      ])
      // Restart process
      return this._setupSymcon()
    }

    if (!this._dominoSwissConfiguratorId) {
      // Need to create one instance of the dominoswiss configurator
      const id = await this.parseKnockautResponse('IPS_CreateInstance', [
        BRELAG_CONFIGURATOR_MODULE_ID,
      ])
      await this.parseKnockautResponse('IPS_SetName', [
        id,
        'Dominoswiss Konfigurator',
      ])
      return this._setupSymcon()
    }

    this.eventHandler = new EventHandler(this)
    this.dashboardHandler = new DashboardHandler(
      this,
      this._knockautConfiguratorId
    )
    this.moduleHandler = new KnockautModuleHandler(this)

    // Load everything else
    this.refreshTree(false)

    if (!this.scriptsFolderId) {
      this.scriptsFolderId = await this.createCategoryRpc(
        { name: SCRIPTS_FOLDER_NAME, parentID: this.rootFolderId },
        true
      )
    }
    if (!this.eventsFolderId) {
      this.eventsFolderId = await this.createCategoryRpc(
        { name: EVENTS_FOLDER_NAME, parentID: this.rootFolderId },
        true
      )
    }
  }

  async getActionForm(
    actionId: string,
    targetId: number,
    parentId: number,
    environment: SymconEnvironment
  ) {
    const actionForm = await this.knockautClient.customRequest(
      'IPS_GetActionForm',
      [
        actionId,
        {
          TARGET: targetId,
          PARENT: parentId,
          ENVIRONMENT: environment,
        },
      ]
    )
    return JSON.parse(actionForm)
  }

  async refreshActions() {
    try {
      const actionsJson = await this.parseKnockautResponse('IPS_GetActions', [])
      this._actions = actionsJson ? JSON.parse(actionsJson) : []
    } catch (error) {
      console.warn(error)
    }
  }

  _displayRowsInUICallback: Function = null

  setDisplayRowsInUI(callback: Function | null) {
    this._displayRowsInUICallback = callback
  }

  async refreshTree(refreshSnapshot: boolean = true) {
    if (refreshSnapshot) {
      this._snapshot = await this.parseKnockautResponse('IPS_GetSnapshot', [])
    }
    this.buildDataTree()
    this.parseSnapshot()
    this.refreshActions()
    this.selectedNodes = []

    // Notify subscribers that data was updated

    console.log('Data', {
      links: this._links,
      deviceGroups: this._deviceGroups,
      dashboards: this._dashboards.value,
      scripts: this._scripts,
      flowscripts: this._flowscripts,
      variables: this._variables,
      categories: this._categories,
    })
    this.refreshSubject.next()
    this._displayRowsInUICallback?.()
  }

  private findProfiles() {
    this._profiles = new Map()
    Object.keys(this._snapshot?.profiles ?? {}).forEach((key) => {
      this._profiles.set(key, this._snapshot.profiles[key])
    })
  }

  private parseSnapshot() {
    this.findProfiles()

    const deviceGroups: KnockautObject[] = []
    const links: KnockautObject[] = []
    const dashboards: KnockautObject[] = []
    const events: KnockautObject[] = []
    const scripts: KnockautObject[] = []
    const flowscripts: KnockautObject[] = []
    const variableMap: Map<number, KnockautObject[]> = new Map()
    const categories: KnockautObject[] = []

    for (const [key, obj] of Object.entries(this._snapshot?.objects ?? {})) {
      if (this.knockautDeviceHandler.isDominoSwissInstance(obj)) {
        // DEVICE GROUP
        deviceGroups.push({
          id: transformIdToNumber(key),
          ...obj,
        })
      } else if (obj.type === SymconObjectType.LINK) {
        // LINK
        links.push({
          id: transformIdToNumber(key),
          ...obj,
        })
      } else if (
        obj.data &&
        obj.data.moduleType === SymconModuleType.VISUALIZER.valueOf() &&
        obj.data.moduleID === DASHBOARD_CONFIGURATOR_MODULE_ID
      ) {
        // DASHBOARDS
        // The 'KnockAutApp' dashboard should not be visible to user
        if (obj.name === 'KnockAutApp') {
          continue
        }
        dashboards.push({
          id: transformIdToNumber(key),
          ...obj,
        })
      } else if (obj.type === SymconObjectType.EVENT.valueOf()) {
        // EVENTS
        events.push({
          id: transformIdToNumber(key),
          ...obj,
        })
      } else if (obj.type === SymconObjectType.SCRIPT.valueOf()) {
        if (obj.data.type === SymconScriptType.FLOW.valueOf()) {
          // FLOW-SCRIPTS
          flowscripts.push({
            id: transformIdToNumber(key),
            ...obj,
          })
        } else {
          // PHP SCRIPTS
          scripts.push({
            id: transformIdToNumber(key),
            ...obj,
          })
        }
      } else if (obj.type === SymconObjectType.VARIABLE.valueOf()) {
        // VARIABLE
        if (!variableMap.has(obj.parentID)) {
          variableMap.set(obj.parentID, [])
        }
        variableMap.get(obj.parentID).push({
          id: transformIdToNumber(key),
          ...obj,
        })
        // Sort
        const variables = variableMap.get(obj.parentID)
        variables.sort((a, b) => a.position - b.position)
        variableMap.set(obj.parentID, variables)
      } else if (obj.type === SymconObjectType.CATEGORY.valueOf()) {
        // CATEGORY
        categories.push({
          id: transformIdToNumber(key),
          ...obj,
        })
      }
    }
    deviceGroups.sort((a, b) => a.position - b.position)
    links.sort((a, b) => a.position - b.position)
    this._deviceGroups = deviceGroups
    this._scripts = scripts
    this._flowscripts = flowscripts
    this._links = links
    this._events = events
    this._categories = categories

    this._dashboards.value = dashboards
    // Find base folders of dashboards
    this.dashboardBaseIds = []
    for (const dashboard of this._dashboards.value) {
      const id = this.dashboardHandler.getDashboardBaseID(dashboard)
      this.dashboardBaseIds.push(id)
    }
    this.dashboardTree = simpleCopy(dashboards)

    this._variables = variableMap
  }

  /**
   * Creates eGate Config and applies it to Symcon
   */
  async syncEgateConfigToSymcon(showSnackbar = (message: string) => {}) {
    // Get eGate Export
    const eGateConfig = await exportConfig(
      this.projectEditor,
      this._fusionOne.id,
      showSnackbar,
      false
    )
    const config64 = btoa(eGateConfig)
    const newConfig = JSON.stringify({
      FileData: config64,
    })
    await this.setConfigurationAndApply(
      this._dominoSwissConfiguratorId,
      newConfig
    )

    // TODO: Does not wait for finish???
    await this.runScriptTextWait(`
      $configuratorId = IPS_GetInstanceListByModuleID("{7979B248-1D5C-4607-8F75-8CB50CD69E34}")[0];
      $form = json_decode(IPS_GetConfigurationForm($configuratorId), true);
      foreach ($form["actions"][0]["values"] as $value) {
          if (isset($value['instanceID']) && ($value['instanceID'] == 0)) {
              if (isset($value["create"])) {
                  //Instanz Erstellen
                  $instanceId = IPS_CreateInstance($value["create"]["moduleID"]);
                  IPS_SetName($instanceId, $value["name"]);
                  IPS_SetPosition($instanceId, $value["create"]["position"]);
                  //Eigenschaften übernehmen
                  IPS_SetConfiguration($instanceId, json_encode($value["create"]["configuration"]));
                  IPS_ApplyChanges($instanceId);
              }
          }
      }`)
    await this.refreshTree(true)
  }

  /**
   * Finds all children for parentID in current snapshot and appends to arr
   * @param arr
   * @param parentID
   */
  private findChildren(arr: KnockautInstance[], parentID: number) {
    for (const [key, value] of Object.entries(this._snapshot?.objects ?? {})) {
      const id = transformIdToNumber(key)
      if (value.parentID === parentID) {
        const children: KnockautInstance[] = []
        // Do not repeat for root folder
        if (id !== 0) {
          this.findChildren(children, id)
        }
        if (id === 0) {
          continue
        }
        if (value.type !== SymconObjectType.INSTANCE) {
          arr.push({
            id,
            text: value.name,
            icon: value.icon,
            opened: true,
            children,
            ...value,
          })
        }
        arr.sort((a, b) => a.position - b.position)
      }
    }
  }

  public getVariablesOfInstance(
    instanceId: number,
    excludeIdents: string[] = []
  ): KnockautObject[] {
    if (this._variables.has(instanceId)) {
      const variables: KnockautObject[] = simpleCopy(
        this._variables.get(instanceId)
      )
      // Filter out list of idents that should be ignored
      return variables.filter((variable) => {
        return excludeIdents.indexOf(variable.ident) < 0
      })
    } else {
      return []
    }
  }

  public hasProfile(key: string): boolean {
    return this._profiles.has(key)
  }

  /**
   * Returns the VariableProfile if available, null otherwise
   */
  public getVariableProfile(variable: SymconObject): RawKnockautProfile | null {
    if (!variable.data) {
      return null
    }
    if (variable.data.customProfile) {
      return this.getProfile(variable.data.customProfile)
    }
    if (variable.data.profile) {
      return this.getProfile(variable.data.profile)
    }
    return null
  }

  public getFormattedValueByProfile(
    value: any,
    variableProfile?: RawKnockautProfile
  ) {
    let formattedValue = null
    if (variableProfile) {
      if (variableProfile.associations?.length > 0) {
        if (value === false) {
          formattedValue = 0
        } else if (value === true) {
          formattedValue = 1
        }
        // See here how it works (section "Assoziationen" -> "Integer/Float:")
        // https://www.symcon.de/service/dokumentation/konzepte/variablen/variablenprofile/
        // TODO: This still does not handle all the cases (e.g. special case for suffix %)
        for (let i = 0; i < variableProfile.associations.length; i++) {
          if (variableProfile.associations[i].value === value) {
            formattedValue = variableProfile.associations[i].name
            break
          } else if (variableProfile.associations[i + 1]) {
            if (
              value > variableProfile.associations[i].value &&
              value < variableProfile.associations[i + 1].value
            ) {
              formattedValue = variableProfile.associations[i].name
            }
          }
        }
      }
      if (!formattedValue) {
        formattedValue = value
      }
      return `${variableProfile.prefix}${formattedValue}${variableProfile.suffix}`
    } else {
      return value
    }
  }

  public getFormattedVariableValue(variable: SymconObject) {
    if (variable.type !== SymconObjectType.VARIABLE) {
      return null
    }
    if (!variable.data) {
      return null
    }
    const value: any = variable.data.value
    const variableProfile: RawKnockautProfile =
      this.getVariableProfile(variable)
    return this.getFormattedValueByProfile(value, variableProfile)
  }

  public getProfile(key: string): RawKnockautProfile {
    return this._profiles.get(key)
  }

  public async getConfiguration(instanceId: number): Promise<string> {
    return this.parseKnockautResponse('IPS_GetConfiguration', [instanceId])
  }

  public async setConfigurationAndApply(
    instanceID: number,
    config: string
  ): Promise<boolean> {
    if (typeof config !== 'string') {
      config = JSON.stringify(config)
    }
    await this.setConfiguration(instanceID, config)
    return this.applyChanges(instanceID)
  }

  public async setConfiguration(
    instanceID: number,
    config: string
  ): Promise<boolean> {
    return this.parseKnockautResponse('IPS_SetConfiguration', [
      instanceID,
      config,
    ])
  }

  public async applyChanges(instanceID: number): Promise<boolean> {
    return this.parseKnockautResponse('IPS_ApplyChanges', [instanceID])
  }

  public async getConfigurationForm(
    instanceId: number
  ): Promise<KnockautConfigurationForm> {
    const response = await this.parseKnockautResponse(
      'IPS_GetConfigurationForm',
      [instanceId]
    )
    return JSON.parse(response)
  }

  public async getScriptContent(id: number) {
    return this.parseKnockautResponse('IPS_GetScriptContent', [id])
  }

  public async getFlowScriptContent(id: number): Promise<string> {
    return this.parseKnockautResponse('KNA_GetFlowScriptData', [
      this._knockautConfiguratorId,
      id,
    ])
  }

  public async runScriptWait(id: number) {
    return this.parseKnockautResponse('IPS_RunScriptWait', [id])
  }

  public async runScriptTextWait(script: string) {
    const res = await this.parseKnockautResponse('IPS_RunScriptTextWait', [
      script,
    ])
    return res
  }

  public async setScriptContent(id: number, content: string) {
    return this.parseKnockautResponse('IPS_SetScriptContent', [id, content])
  }

  public async setHidden(
    instanceID: number,
    hidden: boolean
  ): Promise<boolean> {
    return this.parseKnockautResponse('IPS_SetHidden', [instanceID, hidden])
  }

  public async getChildrenIDs(id: number): Promise<number[]> {
    return this.parseKnockautResponse('IPS_GetChildrenIDs', [id])
  }

  public async moduleExists(guid: string): Promise<boolean> {
    return this.parseKnockautResponse('IPS_LibraryExists', [guid])
  }

  public async getModuleBranchInfos(): Promise<SymconModuleBranchInfo> {
    const branchInfos = await this.parseKnockautResponse('KNA_GetBranchInfos', [
      this._knockautConfiguratorId,
    ])
    return JSON.parse(branchInfos)
  }

  // This function returns true if the module branch was sucessfully changed.
  // It returns false if the branch was already selected.
  // The required parameters can be found in getModuleBranchInfos() response
  public async changeModuleBranch(
    libraryModuleName: string,
    branchName: string
  ): Promise<boolean> {
    return this.parseKnockautResponse('KNA_ChangeBranch', [
      this._knockautConfiguratorId,
      libraryModuleName,
      branchName,
    ])
  }

  public async getConfiguratorsRpc() {
    return this.knockautClient.getConfigurators()
  }

  public getLinks(targetID: number, parentID?: number): KnockautObject[] {
    const links = []
    for (const link of this._links) {
      if (link.data.targetID === targetID) {
        if (typeof parentID === 'undefined') {
          links.push(simpleCopy(link))
        } else {
          if (link.parentID === parentID) {
            links.push(simpleCopy(link))
          }
        }
      }
    }
    return links
  }

  /**
   * This function creates, updates and deletes links in multiple folders.
   * It is mainly used when handling "manual triggers". Manual triggers are links who point to a flowscript (flowscript = Ablaufplan).
   * Those links are represented in the Ultimate App as a Tile. When clicking on it, it executes the action "führe automation aus" on the flowscript.
   * The function receives an original array of ids and a modified one. With this two lists, it compares the list to find out which links are new, modified and deleted.
   *
   * @param originalParentIDs - Original List of ParentIDs for Links. (Folders Where the Links should be placed in)
   * @param parentIDs - Modified List of ParentIDs for Links. (Folders Where the Links should be placed in)
   * @param targetID - Target where all the Links should point to
   * @param name - Name which is the same for all Links
   */
  public async updateLinks(
    originalParentIDs: number[],
    parentIDs: number[],
    targetID: number,
    name: string
  ) {
    enum ACTION {
      CREATE,
      UPDATE,
      DELETE,
    }
    const updateSet = []

    // restructure data to a list, sorted by create, update and delete
    originalParentIDs.forEach((id) => {
      if (parentIDs.includes(id)) {
        updateSet.push({ parentID: id, action: ACTION.UPDATE })
      } else {
        updateSet.push({ parentID: id, action: ACTION.DELETE })
      }
    })

    parentIDs.forEach((id) => {
      if (!originalParentIDs.includes(id)) {
        updateSet.push({ parentID: id, action: ACTION.CREATE })
      }
    })

    // execute actions
    for (const item of updateSet) {
      if (item.action == ACTION.CREATE) {
        // create links which are new
        const linkId = await this.parseKnockautResponse('IPS_CreateLink', [])
        await this.parseKnockautResponse('IPS_SetName', [linkId, name])
        await this.parseKnockautResponse('IPS_SetParent', [
          linkId,
          item.parentID,
        ])
        await this.parseKnockautResponse('IPS_SetLinkTargetID', [
          linkId,
          targetID,
        ])
      } else {
        const links = this.getLinks(targetID, item.parentID)
        for (const id in links) {
          if (item.action == ACTION.UPDATE) {
            // update links which are modified
            const linkId = links[id].id
            await this.parseKnockautResponse('IPS_SetName', [linkId, name])
          } else if (item.action == ACTION.DELETE) {
            // delete links which no longer exist
            this.deleteSymconObject(SymconObjectType.LINK, links[id].id)
          }
        }
      }
    }
    await this.refreshTree()
  }

  /**
   * Deletes an object and all its children
   * @param obj Object to be deleted
   * @param recursionLevel what recursion level we're at
   * @param deletedObjectIds array of already deleted object ids from start of recursion
   * @returns deletedObjectIds
   */
  async deleteSymconObjectRecursive(
    obj: KnockautObject,
    recursionLevel: number = 0,
    deletedObjectIds: number[] = []
  ): Promise<number[]> {
    if (obj.type === SymconObjectType.INSTANCE) {
      if (this.knockautDeviceHandler.isDominoSwissInstance(obj)) {
        await this.knockautDeviceHandler.deleteDeviceGroup(obj)
      }
      // Speed up process for deleting instances by directly deleting variables
      const variables = this.getVariablesOfInstance(obj.id)
      for (const variable of variables) {
        await this.deleteSymconObject(variable.type, variable.id)
        deletedObjectIds.push(variable.id)
      }
    } else if (this.dashboardHandler.isDashboardBaseFolder(obj.id)) {
      // The selected folder is the base folder of a dashboard. Fetch the visualization instance id first
      const visualizationId = this.dashboardHandler.getDashboardIdFromBaseID(
        obj.id
      )
      if (visualizationId === null) {
        throw new Error('Could not find corresponding visualization instance!')
      }
      // Delete the actual visualization first
      await this.dashboardHandler.deleteDashboard(visualizationId)
      // Now the folder and everything else can be deleted
    }

    const children = await this.getChildrenIDs(obj.id)
    if (children.length > 0) {
      for (const childID of children) {
        const child = await this.getObjectById(childID)
        await this.deleteSymconObjectRecursive(
          child,
          recursionLevel + 1,
          deletedObjectIds
        )
      }
    }

    // Delete all links of this object
    const objectLinks = this.getLinks(obj.id)
    for (const link of objectLinks) {
      await this.deleteSymconObject(link.type, link.id)
      deletedObjectIds.push(link.id)
    }

    await this.deleteSymconObject(obj.type, obj.id)
    deletedObjectIds.push(obj.id)
    // Only need to update the whole tree at the end
    if (recursionLevel === 0) {
      await this.refreshTree()
    }
    return deletedObjectIds
  }

  /**
   * Deletes a single symcon object
   * @param type
   * @param id
   */
  async deleteSymconObject(type: number, id: number) {
    let funcName: string
    const additionalParams = []
    if (type === SymconObjectType.CATEGORY.valueOf()) {
      funcName = 'IPS_DeleteCategory'
    } else if (type === SymconObjectType.INSTANCE.valueOf()) {
      funcName = 'IPS_DeleteInstance'
    } else if (type === SymconObjectType.VARIABLE.valueOf()) {
      funcName = 'IPS_DeleteVariable'
    } else if (type === SymconObjectType.LINK.valueOf()) {
      funcName = 'IPS_DeleteLink'
    } else if (type === SymconObjectType.SCRIPT.valueOf()) {
      funcName = 'IPS_DeleteScript'
      // TRUE, wenn Datei endgültig gelöscht werden soll; FALSE, wenn die Datei in den ‘deleted’ Ordner verschoben werden soll
      const deleteFile = true
      additionalParams.push(deleteFile)
    } else if (type === SymconObjectType.EVENT.valueOf()) {
      funcName = 'IPS_DeleteEvent'
    } else if (type === SymconObjectType.MEDIA.valueOf()) {
      funcName = 'IPS_DeleteMedia'
    }
    return this.parseKnockautResponse(funcName, [id, ...additionalParams])
  }

  async setNameSymconObject(id: number, name: string): Promise<boolean> {
    return await this.parseKnockautResponse('IPS_SetName', [id, name], true)
  }

  async setParentId(id: number, parentID: number): Promise<boolean> {
    return await this.parseKnockautResponse(
      'IPS_SetParent',
      [id, parentID],
      true
    )
  }

  async setIcon(id: number, icon: string) {
    await this.parseKnockautResponse('IPS_SetIcon', [id, icon], true)
  }

  async moveObjectPositionUp(obj: KnockautObject) {
    return this.setPosition(obj, obj.position - 1)
  }

  async moveObjectPositionDown(obj: KnockautObject) {
    return this.setPosition(obj, obj.position + 1)
  }

  async setPosition(obj: KnockautObject, position: number) {
    await this.parseKnockautResponse(
      'IPS_SetPosition',
      [obj.id, position],
      true
    )
    await this.refreshTree()
  }

  /**
   * Either returns result or throws error
   * @param response
   */
  async parseKnockautResponse(
    method: string,
    params?: (number | string | boolean)[],
    expectTrue = false
  ): Promise<any> {
    const response = await this.knockautClient?.customRequest(method, params)
    if (expectTrue && !response) {
      throw new Error(
        `Response Error: not perform '${method}' with params: ${params}`
      )
    } else {
      return response
    }
  }

  /**
   * Creates a new symcon object, refreshes the object tree and returns the id of the new object
   * @param model
   */
  async createSymconObject(
    model: CreateSymconObjectModel,
    options?: { scriptType?: SymconScriptType }
  ): Promise<number> {
    let id
    switch (model.type) {
      case SymconObjectType.SCRIPT:
        let scriptType: SymconScriptType = SymconScriptType.PHP
        if (options?.scriptType) {
          scriptType = options.scriptType
        }
        id = await this.parseKnockautResponse('IPS_CreateScript', [
          scriptType.valueOf(),
        ])
        break
      default:
        throw new Error(`Object type ${model.type} not supported yet.`)
    }
    await this.parseKnockautResponse('IPS_SetName', [id, model.name])
    const parentID =
      model.parentID !== null ? model.parentID : this.rootFolderId
    await this.parseKnockautResponse('IPS_SetParent', [id, parentID])
    await this.refreshTree()
    return id
  }

  async createSymconLinks(
    objects: KnockautObject[],
    parentNodeId: string
  ): Promise<void> {
    for (const obj of objects) {
      const linkId = await this.parseKnockautResponse('IPS_CreateLink', [])
      await this.parseKnockautResponse('IPS_SetName', [linkId, obj.name])
      await this.parseKnockautResponse('IPS_SetParent', [linkId, parentNodeId])
      await this.parseKnockautResponse('IPS_SetLinkTargetID', [linkId, obj.id])
    }
    await this.refreshTree()
  }

  async createScriptSymcon(name: string, content: string): Promise<number> {
    // 0 is normal PHP script
    const id = await this.parseKnockautResponse('IPS_CreateScript', [0])
    await this.parseKnockautResponse('IPS_SetName', [id, name])
    await this.setScriptContent(id, content)
    return id
  }

  async createCategoryRpc(
    category: SymconCategory,
    refreshTree: boolean = true
  ): Promise<number> {
    try {
      const id = await this.parseKnockautResponse('IPS_CreateCategory', [])
      await this.parseKnockautResponse('IPS_SetName', [id, category.name])

      let parentID = category.parentID
      if (parentID === null || parentID === undefined) {
        // Use root as parent
        parentID = this.rootFolderId
      }
      await this.parseKnockautResponse('IPS_SetParent', [id, parentID])

      if (refreshTree) {
        await this.refreshTree()
      }
      return id
    } catch (error) {
      throw error
    } finally {
      // TODO: refresh snapshot ?
      console.log('finally')
    }
  }

  /**
   * Creates a dummy module instance (see https://www.symcon.de/service/dokumentation/modulreferenz/dummy-modul/)
   * @param parentID ID of parent
   * @param name name of instance
   * @returns id of created instance
   */
  async createDummyInstance(
    parentID: number = null,
    name: string = 'Dummy Modul'
  ): Promise<number> {
    return this.createInstance(DUMMY_MODULE_ID, parentID, name)
  }

  /**
   * https://www.symcon.de/service/dokumentation/befehlsreferenz/instanzenverwaltung/ips-createinstance/
   * @param moduleId GUID of module
   * @param parentID Parent of instance
   * @param name Name of instance
   * @returns id of newly created instance
   */
  async createInstance(
    moduleId: string,
    parentID: number = null,
    name: string = 'Instanz'
  ): Promise<number> {
    const id = await this.parseKnockautResponse('IPS_CreateInstance', [
      moduleId,
    ])
    if (parentID) {
      await this.parseKnockautResponse('IPS_SetParent', [id, parentID])
    }
    if (name) {
      await this.parseKnockautResponse('IPS_SetName', [id, name])
    }
    await this.refreshTree()
    return id
  }

  public setVariableValue(
    variable: KnockautObject,
    value: boolean | number | string
  ) {
    // TODO: This parsing is used in several places -> Create function
    if (
      variable.data.type === SymconVariableType.INTEGER &&
      typeof value === 'string'
    ) {
      value = parseInt(value)
    } else if (
      variable.data.type === SymconVariableType.FLOAT &&
      typeof value === 'string'
    ) {
      value = parseFloat(value)
    } else if (
      variable.data.type === SymconVariableType.BOOLEAN &&
      (typeof value === 'string' || typeof value === 'number')
    ) {
      if (value === '1' || value === 1) {
        value = true
      } else if (value === '0' || value === 0) {
        value = false
      }
    }
    return this.parseKnockautResponse('RequestAction', [variable.id, value])
  }

  /**
   * Creates a new variable (see https://www.symcon.de/service/dokumentation/befehlsreferenz/variablenverwaltung/ips-createvariable/)
   * @param variableType Type of variable
   * @param value value of variable (has to match the type)
   * @param parentID id of parent for varaible
   * @param name name of variable
   * @returns id of new variable
   */
  async createVariable(
    variableType: SymconVariableType,
    value: SymconVariableValueType = null,
    parentID: number = null,
    name: string = 'Dummy Modul'
  ): Promise<number> {
    const id: number = await this.parseKnockautResponse('IPS_CreateVariable', [
      variableType,
    ])
    if (parentID) {
      await this.parseKnockautResponse('IPS_SetParent', [id, parentID])
    }
    if (name) {
      await this.parseKnockautResponse('IPS_SetName', [id, name])
    }
    await this.refreshTree()
    if (value) {
      const variable = this.getObjectById(id)
      await this.setVariableValue(variable, value)
    }
    return id
  }

  /** LOCATION MODULE **/
  async loadLocation(): Promise<SymconLocation> {
    try {
      const config = JSON.parse(
        await this.getConfiguration(this._locationConfiguratorId)
      )
      return JSON.parse(config.Location)
    } catch (error) {
      console.warn(error)
      return {
        longitude: 0,
        latitude: 0,
      }
    }
  }

  async saveLocation(location: SymconLocation) {
    const config = JSON.parse(
      await this.getConfiguration(this._locationConfiguratorId)
    )
    config.Location = JSON.stringify(location)
    await this.setConfigurationAndApply(
      this._locationConfiguratorId,
      JSON.stringify(config)
    )
  }

  public getEventById(id: number): KnockautEvent {
    return this._getObjectById(id) as KnockautEvent
  }

  public IPS_GetEvent(id: number): Promise<KnockautEvent> {
    return this.parseKnockautResponse('IPS_GetEvent', [id])
  }

  public getObjectById(id: number): KnockautObject {
    return this._getObjectById(id) as KnockautObject
  }

  private _getObjectById(
    id: number
  ): KnockautObject | KnockautEvent | undefined {
    if (this._snapshot?.objects[`ID${id}`]) {
      return {
        ...this._snapshot?.objects[`ID${id}`],
        id,
      }
    } else {
      return null
    }
  }

  private async _remoteGetObjectById(id: number): Promise<KnockautObject> {
    const obj = await this.parseKnockautResponse('IPS_GetObject', [id])
    return obj
  }

  localGetObjectByIdent(
    ident: string,
    parentID: number
  ): KnockautObject | null {
    const children = this.getChildrenFromDataTree(parentID)
    for (const child of children) {
      if (child.ident === ident) {
        return child
      }
    }
    return null
  }

  async getObjectByIdent(
    ident: string,
    parentID: number
  ): Promise<KnockautObject | KnockautEvent | null> {
    const objId = await this.parseKnockautResponse('IPS_GetObjectIDByIdent', [
      ident,
      parentID,
    ])
    return this._getObjectById(objId)
  }

  public isFusionOne(equipmentId: string): boolean {
    return (
      this._fusionOneEquipments.findIndex((id) => id === equipmentId) !== -1
    )
  }

  public get fusionOneEquipmentVariants(): EquipmentVariant[] {
    return this._fusionOneEquipmentVariants
  }

  // TODO: Rename this and parentProject
  get project(): KnockautProject | null {
    return this._knockautProject.value
  }

  get parentProject(): Project | null {
    return this._project
  }

  get senders(): DeviceAssignment[] {
    return this._deviceHandler.senderList
  }

  get levelTree(): DeviceTree[] {
    return this._deviceTree
  }

  async getNetworkInfo(): Promise<SymconNetworkInfo> {
    const response = await this.runScriptTextWait(
      `$info = Sys_GetNetworkInfo();
       print_r(json_encode($info[0]));`
    )
    const jsonResponse = JSON.parse(response)
    return jsonResponse
  }
}
